import { useEffect, useState } from 'react';
import { Icon } from '@/components/generic';

type ThemeButtonProps = {
  className?: string;
};

export const ThemeButton = ({ className }: ThemeButtonProps) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Check if theme is already set in localStorage or system preference
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark' || savedTheme === 'light') {
        return savedTheme;
      }
      // Check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    // Apply theme on mount and when theme changes
    const applyTheme = (newTheme: 'light' | 'dark') => {
      if (newTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
      localStorage.setItem('theme', newTheme);
    };

    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <div
      className={`group flex flex-col gap-1 w-12 p-1.5 bg-b-surface2 rounded-full cursor-pointer transition-all hover:shadow-depth dark:bg-linear-to-b dark:from-[#2A2A2A] dark:to-[#202020] ${
        className || ''
      }`}
      onClick={toggleTheme}
    >
      {(['dark', 'light'] as const).map((themeOption) => (
        <button
          className={`w-9 h-9 rounded-full text-0 transition-colors ${
            theme === themeOption
              ? 'bg-b-surface1 fill-t-primary'
              : 'bg-transparent fill-t-secondary'
          } group-hover:!fill-t-primary`}
          key={themeOption}
        >
          <Icon
            className='!size-4 fill-inherit'
            name={themeOption === 'dark' ? 'moon' : 'sun'}
          />
        </button>
      ))}
    </div>
  );
};
